/**
 * Monetary arithmetic — the client mirror of `backend/apps/core/money.py`.
 *
 * This file exists because a cashier must see a total instantly, including with
 * no network. That figure is **advisory**: the server recomputes every total when
 * the sale is uploaded. But advisory is not the same as approximate — the number
 * printed on the customer's receipt comes from here, so if the two engines
 * disagree by a cent, the receipt disagrees with the ledger.
 *
 * So the two implementations are kept in lockstep by construction:
 *
 *   - the same three rules (no floats, quantize at every boundary, tax per line);
 *   - the same rounding mode (half away from zero);
 *   - the **same fixture table** in both test suites, so a divergence fails a
 *     build rather than surfacing at a till.
 *
 * If you change anything here, change `backend/apps/core/money.py` in the same
 * commit, and update both fixture tables.
 */

import { Decimal } from "decimal.js";

// Half away from zero — the rule a shopkeeper applies by hand, and what
// Python's ROUND_HALF_UP does. `precision` is generous because intermediate
// division (gross × rate / (100 + rate)) needs headroom before quantising.
Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_UP });

/** Two decimal places for money; three for quantity (0.250 kg of mince). */
export const MONEY_PLACES = 2;
export const QUANTITY_PLACES = 3;

export const ZERO = new Decimal("0.00");

/**
 * South Africa withdrew the 1c, 2c and 5c coins, so a cash tender is rounded to
 * the nearest 10c.
 */
export const CASH_INCREMENT_ZAR = new Decimal("0.10");

export const VatMode = {
  /** Not VAT registered. The default — most spaza shops are below the R1m threshold. */
  NONE: "NONE",
  /** Shelf price includes VAT; it is back-calculated. Standard SA retail. */
  INCLUSIVE: "INCLUSIVE",
  /** VAT added on top at the till. What the supplied design mock illustrates. */
  EXCLUSIVE: "EXCLUSIVE",
} as const;

export type VatMode = (typeof VatMode)[keyof typeof VatMode];

export interface TaxSplit {
  net: Decimal;
  tax: Decimal;
  gross: Decimal;
  rate: Decimal;
  mode: VatMode;
}

/** Anything that can be safely turned into money without losing precision. */
export type MoneyInput = Decimal | string | number;

// ═════════════════════════════════════════════════════════════════════════════
// Coercion
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Coerce to a 2-decimal-place monetary Decimal.
 *
 * A non-integer `number` is **rejected**. Every JavaScript number is a float, and
 * a float cannot represent 0.01 exactly — accepting one would let an imprecise
 * value in through a side door and make the resulting cent error impossible to
 * trace. Integers are safe and are accepted, mirroring the backend which accepts
 * `int` but refuses `float`.
 *
 * Money crosses the wire as a **string** (DRF's `COERCE_DECIMAL_TO_STRING`), so
 * in practice API values arrive here already safe.
 */
export function money(value: MoneyInput): Decimal {
  if (typeof value === "number" && !Number.isInteger(value)) {
    throw new TypeError(
      `Refusing to build money from the non-integer number ${value}. ` +
        "Pass a string or a Decimal — floats cannot represent 0.01 exactly.",
    );
  }
  return new Decimal(value).toDecimalPlaces(MONEY_PLACES, Decimal.ROUND_HALF_UP);
}

/** Coerce to a 3-decimal-place quantity, for weighed and measured goods. */
export function quantity(value: MoneyInput): Decimal {
  if (typeof value === "number" && !Number.isInteger(value)) {
    throw new TypeError(
      `Refusing to build a quantity from the non-integer number ${value}. ` +
        "Pass a string or a Decimal.",
    );
  }
  return new Decimal(value).toDecimalPlaces(QUANTITY_PLACES, Decimal.ROUND_HALF_UP);
}

/** `percent` per cent of `amount`, rounded to the cent. */
export function percentageOf(amount: MoneyInput, percent: MoneyInput): Decimal {
  return money(new Decimal(amount).times(percent).dividedBy(100));
}

// ═════════════════════════════════════════════════════════════════════════════
// Tax
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Decompose `amount` into net, tax and gross.
 *
 * The meaning of `amount` follows the mode, mirroring how a merchant thinks
 * about their own prices: it is the **gross** (customer-facing) figure under
 * INCLUSIVE, and the **net** figure under EXCLUSIVE.
 *
 * Rounding is arranged so the three parts always reconcile exactly — the tax is
 * rounded and the third value derived by subtraction, never rounded
 * independently. That is what prevents the classic one-cent receipt discrepancy.
 */
export function splitTax(amount: MoneyInput, rate: MoneyInput, mode: VatMode): TaxSplit {
  const gross0 = money(amount);
  const r = new Decimal(rate);

  if (mode === VatMode.NONE || r.isZero()) {
    return {
      net: gross0,
      tax: ZERO,
      gross: gross0,
      rate: new Decimal("0.00"),
      mode,
    };
  }

  if (mode === VatMode.INCLUSIVE) {
    // tax = gross × rate / (100 + rate).  R142.03 at 15% → R18.53.
    const tax = money(gross0.times(r).dividedBy(r.plus(100)));
    return { net: gross0.minus(tax), tax, gross: gross0, rate: r, mode };
  }

  // EXCLUSIVE
  const tax = money(gross0.times(r).dividedBy(100));
  return { net: gross0, tax, gross: gross0.plus(tax), rate: r, mode };
}

/** Gross figure for a net amount. */
export function addTax(net: MoneyInput, rate: MoneyInput): Decimal {
  return splitTax(net, rate, VatMode.EXCLUSIVE).gross;
}

/** Net figure inside a tax-inclusive amount. */
export function removeTax(gross: MoneyInput, rate: MoneyInput): Decimal {
  return splitTax(gross, rate, VatMode.INCLUSIVE).net;
}

// ═════════════════════════════════════════════════════════════════════════════
// Cash rounding
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Round to the nearest tender-able cash increment.
 *
 * Only the **cash tender** is rounded. The sale total itself is untouched: the
 * difference is recorded on the sale as an explicit `rounding_adjustment`, so the
 * till still balances and the adjustment is reportable rather than being silently
 * absorbed into revenue.
 */
export function roundCash(
  amount: MoneyInput,
  increment: MoneyInput = CASH_INCREMENT_ZAR,
): Decimal {
  const value = money(amount);
  const step = new Decimal(increment);
  if (step.lessThanOrEqualTo(0)) return value;

  const steps = value.dividedBy(step).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  return money(steps.times(step));
}

/**
 * The signed difference introduced by cash rounding. Positive means the customer
 * paid slightly more than the exact total; negative, slightly less.
 */
export function cashRoundingAdjustment(
  amount: MoneyInput,
  increment: MoneyInput = CASH_INCREMENT_ZAR,
): Decimal {
  return money(roundCash(amount, increment).minus(money(amount)));
}

// ═════════════════════════════════════════════════════════════════════════════
// Apportionment
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Split `total` across `weights` so the parts sum to it **exactly**.
 *
 * Needed for transaction-level discounts: a R10 discount on a three-line basket
 * must be distributed across the lines for correct per-product margin and tax
 * reporting, and the parts must still add up to R10. Naive proportional rounding
 * loses cents (3 × 3.33 = 9.99).
 *
 * The remainder is assigned deterministically — largest fractional part first,
 * then largest weight, then earliest index — so a report re-run never produces
 * different per-product margins.
 */
export function apportion(total: MoneyInput, weights: readonly MoneyInput[]): Decimal[] {
  const target = money(total);
  const w = weights.map((x) => new Decimal(x));

  if (w.length === 0) return [];

  const weightSum = w.reduce((acc, x) => acc.plus(x), new Decimal(0));
  if (weightSum.isZero()) return w.map(() => ZERO);

  const raw = w.map((x) => target.times(x).dividedBy(weightSum));
  const parts = raw.map((x) => x.toDecimalPlaces(MONEY_PLACES, Decimal.ROUND_DOWN));

  const distributed = parts.reduce((acc, x) => acc.plus(x), new Decimal(0));
  const centsToGive = target.minus(distributed).times(100).toNumber();

  const order = raw
    .map((value, index) => ({ index, fraction: value.minus(parts[index]!), weight: w[index]! }))
    .sort(
      (a, b) =>
        b.fraction.comparedTo(a.fraction) ||
        b.weight.comparedTo(a.weight) ||
        a.index - b.index,
    );

  for (let i = 0; i < centsToGive; i += 1) {
    const slot = order[i % order.length]!;
    parts[slot.index] = parts[slot.index]!.plus("0.01");
  }

  return parts.map((x) => money(x));
}

export { Decimal };
